import React, { useEffect, useState } from "react";

import supabase from "../../../../utils/supabase";
import useUserStore from "../../../../stores/user";
import { StudentProgressTable } from "./StudentProgressTable";
import { StudentProgressFilters } from "./StudentProgressFilters";
import { StudentProgressPagination } from "./StudentProgressPagination";
import { useStudentProgress } from "./hooks/useStudentProgress";
import { Student } from "./types";
import { useT } from "../../../../stores/uiLang";
import { TD_T } from "../../../../i18n/teacherDashboard";

export function StudentsProgress() {
  const t = useT(TD_T);
  const [classes, setClasses] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [classesLoading, setClassesLoading] = useState(true);
  const [studentsInitialized, setStudentsInitialized] = useState(false);
  const [selectedClass, setSelectedClass] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(5);

  const user = useUserStore((state) => state.user);
  const { students, loading, totalStudents, fetchStudents } =
    useStudentProgress({
      user,
      selectedClass,
      searchTerm,
      currentPage,
      limit,
    });

  async function getClasses() {
    setClassesLoading(true);
    const { data, error } = await supabase
      .from("classes")
      .select("id, name")
      .eq("user_id", user.id);

    if (error) {
      console.error("Error getting classes:", error);
      return [];
    }

    setClassesLoading(false);
    setClasses(data || []);
    return data;
  }

  useEffect(() => {
    getClasses();
  }, [user?.id]);

  useEffect(() => {
    if (classes.length > 0) {
      const timeoutId = setTimeout(() => {
        setStudentsInitialized(true);
        fetchStudents();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [fetchStudents, classes]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleClassChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClass(e.target.value);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    if (classes.length > 0) {
      fetchStudents();
    }
  };

  const totalPages = Math.ceil(totalStudents / limit);

  return (
    <section className="mb-10">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-medium text-gray-900">{t.studentProgress}</h2>
        <StudentProgressFilters
          searchTerm={searchTerm}
          onSearch={handleSearch}
          selectedClass={selectedClass}
          onClassChange={handleClassChange}
          classes={classes}
          onRefresh={handleRefresh}
          loading={loading}
          hasClasses={classes.length > 0}
        />
      </div>

      <StudentProgressTable
        students={students}
        loading={loading}
        classesLoading={classesLoading}
        classes={classes}
        studentsInitialized={studentsInitialized}
      />

      <StudentProgressPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalStudents={totalStudents}
        limit={limit}
        onPageChange={setCurrentPage}
      />
    </section>
  );
}
